// cs2065 (Ilda Corretora — imóveis no RS/SC e na Flórida): na busca do site, PAÍS e ESTADO
// viram dois filtros separados. Antes era um select só, com "Flórida - Estados Unidos".
//
// Pedido de UMA licença em código dos ~300 sites ⇒ allowlist por codsite. Os outros tenants
// com filtro de Estado (2177/2297/1942) seguem exatamente como estão.
//
// De onde vem o país: a query `estados { id nome pais }` do site-graphql já traz o nome do país
// de cada estado (`estados.ds_pais`) e só lista estado com imóvel publicado — a lista de países
// é derivada dela, sem query nova e sem mexer no backend.
//
// Como a busca filtra por país SEM estado escolhido: pelas cidades daqueles estados
// (`id_cidades`, o mesmo argumento das regiões multi-filiais). As cidades (`allCities`) já
// chegam do SSR com `id_estado`.

import { removerAcentos } from './FunctionsGlobals';
import { getHomeScopedStorageKey } from './homeCache';
import { getWithTTL, setWithTTL } from './storage';

export const CODSITES_FILTRO_PAIS = new Set<number>([2065]);
export const PAIS_QUERY_PARAM = 'pais';

export const temFiltroPais = (siteId: unknown): boolean => CODSITES_FILTRO_PAIS.has(Number(siteId));

export type EstadoComPais = { label: string; value: number; pais: string; paisSlug: string };
export type OpcaoPais = { label: string; value: string };

// "Estados Unidos" → "estados-unidos" (é o que vai na URL: ?pais=estados-unidos).
export const slugPais = (nome: unknown): string =>
    removerAcentos(String(nome ?? ''))
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

/** `estados` do site-graphql → opções do select de Estado. O rótulo é SÓ o estado. */
export const formatEstadosComPais = (estados: unknown): EstadoComPais[] => {
    if (!Array.isArray(estados)) return [];
    return estados
        .map((e: any) => {
            const id = Number(e?.id);
            const nome = String(e?.nome ?? '').trim();
            if (!Number.isInteger(id) || id <= 0 || !nome) return null;
            const pais = String(e?.pais ?? '').trim();
            return { label: nome, value: id, pais, paisSlug: slugPais(pais) };
        })
        .filter(Boolean) as EstadoComPais[];
};

const ehListaComPais = (lista: unknown): lista is EstadoComPais[] =>
    Array.isArray(lista) &&
    lista.length > 0 &&
    lista.every((e: any) => e && typeof e.paisSlug === 'string' && Number(e.value) > 0);

/** Países com imóvel publicado (derivados dos estados), em ordem alfabética. */
export const listarPaises = (estados: unknown): OpcaoPais[] => {
    if (!Array.isArray(estados)) return [];
    const vistos = new Map<string, OpcaoPais>();
    for (const e of estados as any[]) {
        const slug = e?.paisSlug;
        if (typeof slug !== 'string' || !slug || vistos.has(slug)) continue;
        vistos.set(slug, { label: String(e?.pais || slug), value: slug });
    }
    return Array.from(vistos.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
};

/** Estados daquele país (sem país escolhido, todos). */
export const estadosDoPais = <T extends { paisSlug?: string } = EstadoComPais>(estados: unknown, paisSlug?: string | null): T[] => {
    if (!Array.isArray(estados)) return [];
    if (!paisSlug) return estados as T[];
    return (estados as T[]).filter((e) => e?.paisSlug === paisSlug);
};

export const paisDoEstado = (estados: unknown, idEstado: unknown): OpcaoPais | null => {
    const id = Number(idEstado);
    if (!Array.isArray(estados) || !Number.isInteger(id) || id <= 0) return null;
    const estado = (estados as any[]).find((e) => Number(e?.value) === id);
    return estado?.paisSlug ? { label: String(estado.pais || estado.paisSlug), value: estado.paisSlug } : null;
};

/** Cidades (com `id_estado`, como vêm do SSR) que ficam nos estados daquele país. Sem país, todas. */
export const cidadesDoPais = <T extends { id_estado?: any }>(
    cidades: T[] | null | undefined,
    estados: unknown,
    paisSlug?: string | null
): T[] => {
    if (!Array.isArray(cidades)) return [];
    if (!paisSlug) return cidades;
    const idsEstados = new Set(estadosDoPais<any>(estados, paisSlug).map((e) => Number(e?.value)));
    return cidades.filter((c) => idsEstados.has(Number(c?.id_estado)));
};

/** Ids das cidades do país — vão no `id_cidades` da busca. Sem país, lista vazia. */
export const idsCidadesDoPais = (
    cidades: Array<{ id_estado?: any; value?: any }> | null | undefined,
    estados: unknown,
    paisSlug?: string | null
): number[] => {
    if (!paisSlug) return [];
    return cidadesDoPais(cidades, estados, paisSlug)
        .map((c) => Number(c?.value))
        .filter((n) => Number.isInteger(n) && n > 0);
};

/**
 * País e Estado a partir da URL (`?pais=<slug>&id_estado=<id>`). Estado de OUTRO país que o da
 * URL é descartado (o país vence — link editado à mão). Sem `pais`, o país sai do estado.
 * País que não existe mais na lista = sem filtro de país (link antigo não vira "0 imóveis").
 */
export const resolverPaisEstado = (
    estados: unknown,
    paisSlug: unknown,
    idEstado: unknown
): { pais: OpcaoPais | null; estado: EstadoComPais | null } => {
    const lista = Array.isArray(estados) ? (estados as EstadoComPais[]) : [];
    const id = Number(idEstado);
    let estado = Number.isInteger(id) && id > 0 ? lista.find((e) => Number(e?.value) === id) || null : null;
    const slug = typeof paisSlug === 'string' ? slugPais(paisSlug) : '';
    let pais = slug ? listarPaises(lista).find((p) => p.value === slug) || null : null;
    if (pais && estado && estado.paisSlug !== pais.value) estado = null;
    if (!pais && estado) pais = paisDoEstado(lista, estado.value);
    return { pais, estado };
};

const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * Chave PRÓPRIA, com validade de 1 dia. A lista antiga guardada no navegador do visitante
 * (`imoveis_list_states_v2`: "Flórida - Estados Unidos", sem país separado e sem validade)
 * fica para trás sozinha — e país novo cadastrado pela imobiliária aparece em até 1 dia.
 */
export const getEstadosComPaisStorageKey = (host: unknown, codsite: unknown): string =>
    getHomeScopedStorageKey('imoveis_list_states_pais_v1', host, codsite);

export const carregarEstadosComPais = async (
    chave: string,
    buscar: () => Promise<any>
): Promise<EstadoComPais[]> => {
    const guardada = getWithTTL<EstadoComPais[]>(chave);
    if (ehListaComPais(guardada)) return guardada;
    const lista = formatEstadosComPais((await buscar())?.estados);
    if (lista.length > 0) setWithTTL(chave, lista, DIA_MS);
    return lista;
};
